import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown, Building2, Cpu, HardHat, Zap, Globe, Hammer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const getMenuIcon = (index: number) => {
  const icons = [Building2, Cpu, HardHat, Zap, Globe, Hammer];
  return icons[index % icons.length];
};
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  return <>
      <header className={cn("sticky top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b", scrolled ? "bg-background/85 backdrop-blur-lg border-border shadow-sm" : "bg-background border-transparent")}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/" className="block">
              <img src="/img/nucetech_brand_logo.png" alt="NUCETECH Logo" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex items-center justify-end px-8 ml-auto">
            {!loading && <NavigationMenu>
                <NavigationMenuList className="gap-2">
                  {menuItems.map(item => <NavigationMenuItem key={item.id}>
                      {item.children && item.children.length > 0 ? <>
                          <NavigationMenuTrigger className="bg-transparent hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent text-foreground font-medium text-[15px]">
                            {item.label}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                              {item.children.map((child, idx) => {
                        const Icon = getMenuIcon(idx);
                        return <li key={child.id} data-index={idx}>
                                    <NavigationMenuLink asChild>
                                      <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent group">
                                        <div className="flex items-center gap-2 text-sm font-medium leading-none text-foreground group-hover:text-accent">
                                          <Icon className="h-4 w-4 text-accent" />
                                          {child.label}
                                        </div>
                                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1.5">
                                          Advanced solutions for modern construction challenges.
                                        </p>
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>;
                      })}
                            </ul>
                          </NavigationMenuContent>
                        </> : <NavigationMenuLink asChild>
                          <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-[15px] font-medium transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
                            {item.label}
                          </Link>
                        </NavigationMenuLink>}
                    </NavigationMenuItem>)}
                </NavigationMenuList>
              </NavigationMenu>}
          </div>

          {}
          <div className="hidden lg:flex items-center gap-4">
            <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
              <Link to="/contact-us">Contact Us</Link>
            </Button>
          </div>

          {}
          <div className="lg:hidden">
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu} className="text-foreground hover:text-accent hover:bg-accent/10">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </header>

      {}
      <div className={cn("fixed inset-0 z-40 bg-background/95 backdrop-blur-xl transition-transform duration-300 lg:hidden pt-24 px-6 overflow-y-auto", isMobileMenuOpen ? "translate-x-0" : "translate-x-full")}>
        <nav className="flex flex-col space-y-6">
          {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/50 pb-4 last:border-0">
              {item.children && item.children.length > 0 ? <div className="space-y-3">
                  <div className="font-semibold text-lg text-foreground">{item.label}</div>
                  <div className="pl-4 grid gap-3">
                    {item.children.map((child, idx) => <Link key={child.id} to={child.href || "#"} className="flex items-center gap-3 text-muted-foreground hover:text-accent transition-colors py-1" onClick={() => setIsMobileMenuOpen(false)}>
                        <ChevronDown className="h-3 w-3 -rotate-90" />
                        {child.label}
                      </Link>)}
                  </div>
                </div> : <Link to={item.href || "#"} className="block text-lg font-semibold text-foreground hover:text-accent transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                  {item.label}
                </Link>}
            </div>)}
          <div className="pt-4">
            <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-lg">
              <Link to="/contact-us" onClick={() => setIsMobileMenuOpen(false)}>
                Contact Us
              </Link>
            </Button>
          </div>
        </nav>
      </div>
    </>;
}
export default function Header() {
  return <MenuProvider menu_id="86" className="w-full">
      <HeaderContent />
    </MenuProvider>;
}